import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { format } from 'date-fns';
import { FiDollarSign, FiTrendingUp, FiUsers, FiRefreshCw, FiSearch, FiClock } from 'react-icons/fi';
import '../styles/Economy.css';

const fmt = (n) => (n || 0).toLocaleString('vi-VN');

const StatCard = ({ icon, label, value, sub, color }) => (
    <div className="eco-stat-card" style={{ borderLeftColor: color }}>
        <div className="eco-stat-icon" style={{ color }}>{icon}</div>
        <div className="eco-stat-body">
            <div className="eco-stat-label">{label}</div>
            <div className="eco-stat-value">{value}</div>
            {sub && <div className="eco-stat-sub">{sub}</div>}
        </div>
    </div>
);

const Economy = () => {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch]   = useState('');
    const [limit, setLimit]     = useState(100);

    const fetchData = useCallback(async (lim = limit) => {
        try {
            setLoading(true);
            const res = await api.get(`/api/stats/money/leaderboard?limit=${lim}`);
            setData(res.data);
        } catch (e) {
            toast.error('Không thể tải bảng xếp hạng');
        } finally {
            setLoading(false);
        }
    }, [limit]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const players = (data?.players || []).filter(p => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (p.name || '').toLowerCase().includes(q) ||
               (p.citizenId || '').toLowerCase().includes(q);
    });

    const totalAmount = players.reduce((s, p) => s + (p.amount || 0), 0);
    const top1 = players[0];

    return (
        <div className="economy-page">
            <div className="eco-header">
                <div>
                    <h1>Kinh Tế Server</h1>
                    <p className="eco-subtitle">
                        {data?.source === 'fivem' ? (
                            <span className="eco-source-badge fivem">⚡ FiveM Realtime</span>
                        ) : (
                            <span className="eco-source-badge discord">💬 Discord #tổng-tiền</span>
                        )}
                        {data?.latestUpdate && (
                            <span className="eco-subtitle-time">
                                {' '}· cập nhật {format(new Date(data.latestUpdate), 'HH:mm dd/MM/yyyy')}
                            </span>
                        )}
                    </p>
                </div>
                <button className="eco-refresh-btn" onClick={() => fetchData()} disabled={loading}>
                    <FiRefreshCw className={loading ? 'spinning' : ''} />
                    Làm mới
                </button>
            </div>

            {/* Stats row */}
            <div className="eco-stats-row">
                <StatCard
                    icon={<FiUsers />}
                    label="Người chơi theo dõi"
                    value={data?.totalPlayers ?? '—'}
                    sub="trong bảng xếp hạng"
                    color="#f0b429"
                />
                <StatCard
                    icon={<FiDollarSign />}
                    label="Người giàu nhất"
                    value={top1 ? `$${fmt(top1.amount)}` : '—'}
                    sub={top1?.name || ''}
                    color="#34d399"
                />
                <StatCard
                    icon={<FiTrendingUp />}
                    label="Tổng tiền lưu thông"
                    value={`$${fmt(totalAmount)}`}
                    sub={`${players.length} người chơi`}
                    color="#60a5fa"
                />
                <StatCard
                    icon={<FiClock />}
                    label="Cập nhật lần cuối"
                    value={data?.latestUpdate ? format(new Date(data.latestUpdate), 'HH:mm') : '—'}
                    sub={data?.latestUpdate ? format(new Date(data.latestUpdate), 'dd/MM/yyyy') : ''}
                    color="#a78bfa"
                />
            </div>

            {/* Table */}
            <div className="eco-table-section">
                <div className="eco-table-header">
                    <div className="eco-table-title">
                        TOP {limit} NGƯỜI GIÀU NHẤT THÀNH PHỐ
                    </div>
                    <div className="eco-search-wrap">
                        <FiSearch className="eco-search-icon" />
                        <input
                            type="text"
                            placeholder="Tìm tên, Citizen ID..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="eco-search"
                        />
                    </div>
                    <select className="eco-limit-sel" value={limit} onChange={(e) => { setLimit(+e.target.value); fetchData(+e.target.value); }}>
                        <option value={50}>Top 50</option>
                        <option value={100}>Top 100</option>
                        <option value={200}>Top 200</option>
                    </select>
                </div>

                {loading ? (
                    <div className="eco-loading">
                        <div className="loading-spinner"></div>
                        <p>Đang tải...</p>
                    </div>
                ) : players.length === 0 ? (
                    <div className="eco-empty">
                        Không có dữ liệu. Bot cần nhận được ít nhất một message từ kênh <code>#tổng-tiền</code>.
                    </div>
                ) : (
                    <div className="eco-table-wrap">
                        <table className="eco-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 50, textAlign: 'center' }}>#</th>
                                    <th>Người Chơi</th>
                                    <th style={{ width: 160 }}>Citizen ID</th>
                                    <th style={{ width: 180, textAlign: 'right' }}>Tổng Số Tiền</th>
                                    <th style={{ width: 130 }}>Lần cuối thấy</th>
                                </tr>
                            </thead>
                            <tbody>
                                {players.map((p, i) => (
                                    <tr key={p.citizenId || i} className="eco-row">
                                        <td className="eco-rank">
                                            {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                                        </td>
                                        <td className="eco-name">
                                            {p.citizenId
                                                ? <Link to={`/fivem-player/${encodeURIComponent(p.citizenId)}`} className="eco-name-link">
                                                    👤 {p.name || p.citizenId}
                                                  </Link>
                                                : (p.name || '—')
                                            }
                                        </td>
                                        <td className="eco-steam">{p.citizenId || '—'}</td>
                                        <td className="eco-total" style={{ textAlign: 'right' }}>
                                            ${fmt(p.amount)}
                                        </td>
                                        <td className="eco-time">
                                            {p.lastSeen ? format(new Date(p.lastSeen), 'HH:mm dd/MM') : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="eco-table-footer">
                    Hiển thị {players.length} người chơi
                    {search && ` (lọc từ ${data?.players?.length || 0})`}
                </div>
            </div>
        </div>
    );
};

export default Economy;
